import React, { useState } from "react";
import { Leaf, Sprout, CheckCircle2, ArrowRight, DollarSign, Shield, MapPin } from "lucide-react";
import { Helmet } from "react-helmet";
import app from "./firebase";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";

const db = getFirestore(app);

export default function SellerLandingPage() {
  const [formData, setFormData] = useState({
    crop: "Cocoa Beans",
    volume: "",
    harvestDate: "",
    location: "",
    targetPrice: "",
    fullName: "",
    phone: "",
    cooperative: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, "seller_leads"), {
        ...formData,
        createdAt: serverTimestamp(),
        source: "seller_landing"
      });
    } catch (err) {
      console.warn("Firestore save fallback:", err);
    }
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div style={{ fontFamily: "var(--font-sans, system-ui, sans-serif)", color: "#1e293b", background: "#f8fafc", minHeight: "100vh" }}>
      <Helmet>
        <title>Sell Produce to Bulk Buyers — Free Buyer Demand Check | Agrivestia</title>
        <meta name="description" content="Farmers and agricultural commodity suppliers: get matched with verified corporate buyers, exporters, and local processors with guaranteed escrow payments." />
        <meta name="keywords" content="sell crops online Nigeria, bulk crop buyers Africa, sell cocoa beans, sell raw cashew, farmer marketplace Nigeria" />
      </Helmet>

      {/* Header */}
      <header style={{ background: "#ffffff", borderBottom: "1px solid #e2e8f0", padding: "16px 24px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: "8px", textDecoration: "none" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Leaf size={20} color="white" />
            </div>
            <span style={{ fontSize: "20px", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>AGRIVESTIA <span style={{ color: "#16a34a", fontSize: "14px", fontWeight: 700 }}>SUPPLIERS</span></span>
          </a>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <a href="/central_market" style={{ color: "#475569", textDecoration: "none", fontSize: "14px", fontWeight: 600 }}>Live Tenders</a>
            <a href="#demand-check" style={{ background: "#16a34a", color: "#ffffff", padding: "10px 20px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, fontSize: "14px" }}>Check Buyer Demand</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section style={{ background: "linear-gradient(180deg, #052e16 0%, #064e3b 100%)", color: "#ffffff", padding: "60px 24px 80px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "48px", alignItems: "center" }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(34,197,94,0.2)", border: "1px solid rgba(34,197,94,0.4)", padding: "6px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: 700, color: "#4ade80", marginBottom: "20px" }}>
              <Sprout size={16} /> Farmers & Crop Aggregators Portal
            </div>
            <h1 style={{ fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 900, lineHeight: 1.15, marginBottom: "20px", letterSpacing: "-0.02em" }}>
              Find Verified Bulk Buyers for Your Harvest
            </h1>
            <p style={{ fontSize: "18px", color: "#a7f3d0", lineHeight: 1.6, marginBottom: "32px" }}>
              Skip predatory middlemen. Connect directly with corporate processors and export buyers ready to purchase your crops at fair market prices with secure escrow payment guarantees.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", fontSize: "14px", color: "#ecfdf5" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#34d399" /> Guaranteed Payment via Escrow</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#34d399" /> Direct Warehouse/Farmgate Pickups</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#34d399" /> Zero Upfront Fee</div>
            </div>
          </div>

          {/* Form */}
          <div id="demand-check" style={{ background: "#ffffff", borderRadius: "16px", padding: "32px", color: "#0f172a", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.3)" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "32px 16px" }}>
                <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "#dcfce7", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <CheckCircle2 size={36} />
                </div>
                <h3 style={{ fontSize: "24px", fontWeight: 800, marginBottom: "12px" }}>Demand Check Submitted!</h3>
                <p style={{ color: "#475569", fontSize: "15px", lineHeight: 1.6, marginBottom: "24px" }}>
                  Thank you, <strong>{formData.fullName}</strong>. We have logged your offer for <strong>{formData.volume} {formData.crop}</strong> in {formData.location}. Our field desk will check matching buyer orders and send you instant buyer quotes on WhatsApp.
                </p>
                <a href="/central_market" style={{ background: "#16a34a", color: "#ffffff", padding: "12px 24px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, display: "inline-block" }}>
                  Browse Active Buyer Tenders Now &rarr;
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", marginBottom: "4px" }}>Get a Free Buyer-Demand Check</h2>
                <p style={{ fontSize: "13px", color: "#64748b", margin: "-8px 0 8px" }}>See how many verified buyers are currently seeking your crop.</p>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Crop Available *</label>
                    <select name="crop" value={formData.crop} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", background: "#ffffff" }}>
                      <option>Cocoa Beans</option>
                      <option>Raw Cashew Nuts</option>
                      <option>White Sesame Seeds</option>
                      <option>Paddy Rice</option>
                      <option>Yellow Maize</option>
                      <option>Split Dried Ginger</option>
                      <option>Cassava Chips / Flakes</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Available Volume *</label>
                    <input type="text" name="volume" required placeholder="e.g. 10 MT or 200 Bags" value={formData.volume} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Harvest / Availability Date *</label>
                    <input type="text" name="harvestDate" required placeholder="e.g. Next Week / Available Now" value={formData.harvestDate} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Farm / Warehouse Location *</label>
                    <input type="text" name="location" required placeholder="e.g. Akure, Ondo State" value={formData.location} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Target Selling Price</label>
                    <input type="text" name="targetPrice" placeholder="e.g. Market price / ₦/Ton" value={formData.targetPrice} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Cooperative / Company (Optional)</label>
                    <input type="text" name="cooperative" placeholder="Ondo Cocoa Farmers Coop" value={formData.cooperative} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Your Full Name *</label>
                  <input type="text" name="fullName" required placeholder="Emeka Nwosu" value={formData.fullName} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>WhatsApp / Phone Number *</label>
                  <input type="tel" name="phone" required placeholder="+234 802 345 6789" value={formData.phone} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                </div>

                <button type="submit" disabled={loading} style={{ background: "#16a34a", color: "#ffffff", border: "none", padding: "14px", borderRadius: "8px", fontWeight: 800, fontSize: "16px", cursor: "pointer", marginTop: "8px", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                  {loading ? "Checking Buyer Demand..." : "Check Buyer Demand Free"} <ArrowRight size={18} />
                </button>
                <div style={{ fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>
                  🔒 Free demand check. No long forms or mandatory registration required upfront.
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section style={{ padding: "80px 24px", maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "48px" }}>
          <h2 style={{ fontSize: "32px", fontWeight: 900, color: "#0f172a", marginBottom: "12px" }}>Why Farmers & Aggregators Sell on Agrivestia</h2>
          <p style={{ color: "#64748b", fontSize: "16px" }}>Empowering primary agricultural producers across West Africa with transparent trade.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px" }}>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
              <DollarSign size={24} />
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Higher Profit Margins</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>By selling directly to verified exporters and processors, suppliers earn up to 15–25% higher returns compared to local village brokers.</p>
          </div>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
              <Shield size={24} />
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Guaranteed Escrow Payout</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Never worry about bounced checks or delayed buyer payment. Funds are pre-committed in Agrivestia escrow before you release your produce.</p>
          </div>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
              <MapPin size={24} />
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Warehouse & Transport Support</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Our logistics partners organize village aggregation pickup and transport to port terminals when selling in volume.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: "#052e16", color: "#94a3b8", padding: "40px 24px", textAlign: "center", fontSize: "14px" }}>
        <p>&copy; 2026 Agrivestia Supplier Portal. All rights reserved. | <a href="/" style={{ color: "#4ade80", textDecoration: "none" }}>Homepage</a> | <a href="/central_market" style={{ color: "#4ade80", textDecoration: "none" }}>Central Market</a></p>
      </footer>
    </div>
  );
}
